import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { GenericService } from '../../../../core/services/GenericService';
import { ApiService } from '../../../../core/services/ApiService';
import { ITableResults } from '../../../../core/interfaces';
import type { IProductionArticle } from '../interfaces';
import { QUERY_GET_ALL } from '../query';

@Injectable({ providedIn: 'root' })
export class ProductionArticlesService extends GenericService<IProductionArticle> {

  protected baseUrl = '/production-articles';

  private _cache: IProductionArticle[] = [];
  private _full = false;
  private _inflight$: Observable<IProductionArticle[]> | null = null;

  getQueryableAll(pageIndex: number,
                  pageSize: number,
                  sortField: string | null,
                  sortOrder: string | null,
                  filters: Array<{ key: string; value: string[] | string }>,
                  search: Array<{ key: string; value: string[] | string }>,
                  additionalFilters: Array<{ key: string; value: string[] | string }>
  ): Observable<ITableResults<IProductionArticle>> {
    return super.getQueryableAll(pageIndex, pageSize, sortField, sortOrder, filters, search, additionalFilters).pipe(
      tap(res => this.mergeIntoCache(res.results))
    );
  }

  getById(id: string): Observable<IProductionArticle> {
    return super.getById(id).pipe(
      tap(article => this.mergeIntoCache([article]))
    );
  }

  findById(id: number): IProductionArticle | null {
    return this._cache.find(a => a.id === id) ?? null;
  }

  listCached(): IProductionArticle[] {
    return this.sortByName(this._cache);
  }

  warmCache(): Observable<IProductionArticle[]> {
    if (this._full) {
      return of(this.listCached());
    }
    if (!this._inflight$) {
      this._inflight$ = this.getQueryableAll(1, 10, 'name', 'ascend', [], [], QUERY_GET_ALL).pipe(
        map(res => {
          this._cache = this.sortByName(res.results ?? []);
          this._full = true;
          return this.listCached();
        }),
        tap(() => { this._inflight$ = null; }),
        catchError(err => {
          this._inflight$ = null;
          return throwError(() => err);
        }),
        shareReplay(1)
      );
    }
    return this._inflight$;
  }

  searchForSelect(query: string): Observable<IProductionArticle[]> {
    const q = (query ?? '').trim();
    return this.warmCache().pipe(
      map(rows => q ? rows.filter(article => this.matchesArticle(article, q)) : rows)
    );
  }

  constructor(apiService: ApiService) {
    super(apiService);
    this.warmCache().subscribe({ error: () => undefined });
  }

  private matchesArticle(article: IProductionArticle, query: string): boolean {
    return this.matchesSearch(article.name, query)
      || this.matchesSearch(article.shopwareArticleName, query);
  }

  private matchesSearch(haystack: string | null | undefined, query: string): boolean {
    const hay = this.normalizeSearch(haystack);
    const q = this.normalizeSearch(query);
    if (!q) {
      return true;
    }
    if (!hay) {
      return false;
    }
    if (hay.includes(q)) {
      return true;
    }
    return q.split(/\s+/).every(token => hay.includes(token));
  }

  private normalizeSearch(value: string | null | undefined): string {
    return (value ?? '')
      .toLowerCase()
      .replace(/[-–—]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private sortByName(articles: IProductionArticle[]): IProductionArticle[] {
    return [...articles].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'de'));
  }

  private mergeIntoCache(articles: IProductionArticle[]): void {
    const incoming = (articles ?? []).filter(a => !!a);
    const incomingIds = new Set(incoming.map(a => a.id));
    this._cache = [
      ...this._cache.filter(a => !incomingIds.has(a.id)),
      ...incoming
    ];
  }
}
